import React from 'react';
import { Box, Typography } from '@mui/material';
import { formatUnits } from 'ethers/lib/utils';
import { TokenIcon } from 'src/components/primitives/TokenIcon';

import { DarkTooltip } from '../../../components/infoTooltips/DarkTooltip';
import { FormattedNumber } from '../../../components/primitives/FormattedNumber';
import { EOperationType, ITransactionHistoryItem } from '../types';
import { fetchIconSymbolAndNameHistorical } from '../helpers';

export const ActionTextMap = ({ operationType }: { operationType: EOperationType }) => {
  switch (operationType) {
    case EOperationType.Supply:
      return <>Supply</>
    case EOperationType.Borrow:
      return <>Borrow</>
    case EOperationType.Withdraw:
      return <>Withdraw</>
    case EOperationType.Repay:
      return <>Repay</>
    case EOperationType.SupplyErc1155:
      return <>Supply NFT</>
    case EOperationType.WithdrawErc1155:
      return <>Withdraw NFT</>
    case EOperationType.Leverage:
      return <>Leverage</>
    case EOperationType.CreateAndLeverage:
      return <>Create and Leverage</>
    case EOperationType.Deleverage:
      return <>Deleverage</>
    case EOperationType.ClaimFees:
      return <>Claim Fees</>
    case EOperationType.Compound:
      return <>Compound</>
    case EOperationType.Rebalance:
      return <>Rebalance</>
    case EOperationType.ClosePosition:
      return <>Close Position</>
    case EOperationType.LeverageLiquidate:
      return <>Liquidation</>
    default:
      return <></>;
  }
};

export const ActionDetails = ({ transaction, iconSize }: { transaction: ITransactionHistoryItem; iconSize: string; }) => {
  switch (transaction.operationType) {
    case EOperationType.Supply:
      const formattedSupplyReserve = fetchIconSymbolAndNameHistorical(transaction.reserve!);
      const formattedSupplyAmount = formatUnits(transaction.amount, transaction.reserve!.decimals);
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TokenIcon symbol={formattedSupplyReserve.iconSymbol} sx={{ fontSize: iconSize }}/>
          <Typography
            variant="secondary14"
            color="text.primary"
            sx={{ ml: formattedSupplyReserve.iconSymbol.split('_').length > 1 ? 3 : 1, mb: 0.5 }}
          >
            +
          </Typography>
          <DarkTooltip
            wrap
            title={
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{ display: 'flex' }}>
                  <FormattedNumber
                    value={formattedSupplyAmount}
                    variant="secondary14"
                    color="common.white"
                    sx={{ mr: 1 }}
                  />
                  <Typography variant="secondary14" color="common.white">
                    {formattedSupplyReserve.symbol}
                  </Typography>
                </Box>
              </Box>
            }
            arrow
            placement="top"
          >
            <Box>
              <FormattedNumber
                value={formattedSupplyAmount}
                variant="secondary14"
                color="text.primary"
                compact
                compactThreshold={100000}
                sx={{ mr: 1 }}
              />
            </Box>
          </DarkTooltip>
          <DarkTooltip
            title={
              <Typography variant="secondary14" color="common.white">
                {formattedSupplyReserve.name} ({formattedSupplyReserve.symbol})
              </Typography>
            }
            arrow
            placement="top"
          >
            <Typography variant="secondary14" color="text.primary">
              {formattedSupplyReserve.symbol}
            </Typography>
          </DarkTooltip>
        </Box>
      );
    case EOperationType.Borrow:
      const formattedBorrowReserve = fetchIconSymbolAndNameHistorical(transaction.reserve!);
      const formattedBorrowAmount = formatUnits(transaction.amount, transaction.reserve!.decimals);
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <TokenIcon symbol={formattedBorrowReserve.iconSymbol} sx={{ fontSIze: iconSize }}/>
          <Typography
            variant="secondary14"
            color="text.primary"
            sx={{ ml: formattedBorrowReserve.iconSymbol.split('_').length > 1 ? 3 : 1, mb: 0.5 }}
          >
            &minus;
          </Typography>
          <DarkTooltip
            wrap
            title={
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{ display: 'flex' }}>
                  <FormattedNumber
                    value={formattedBorrowAmount}
                    variant="secondary14"
                    color="common.white"
                    sx={{ mr: 1 }}
                  />
                  <Typography variant="secondary14" color="common.white">
                    {formattedBorrowReserve.symbol}
                  </Typography>
                </Box>
              </Box>
            }
            arrow
            placement="top"
          >
            <Box>
              <FormattedNumber
                value={formattedBorrowAmount}
                variant="secondary14"
                color="text.primary"
                sx={{ mr: 1 }}
                compact
                compactThreshold={100000}
              />
            </Box>
          </DarkTooltip>
          <DarkTooltip
            title={
              <Typography variant="secondary14" color="common.white">
                {formattedBorrowReserve.name} ({formattedBorrowReserve.symbol})
              </Typography>
            }
            arrow
            placement="top"
          >
            <Typography variant="secondary14" color="text.primary">
              {formattedBorrowReserve.symbol}
            </Typography>
          </DarkTooltip>
        </Box>
      );
    case EOperationType.Withdraw:
      const formattedWithdrawReserve = fetchIconSymbolAndNameHistorical(transaction.reserve!);
      const formattedWithdrawAmount = formatUnits(transaction.amount, transaction.reserve!.decimals);
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <TokenIcon symbol={formattedWithdrawReserve.iconSymbol} sx={{ fontSIze: iconSize }}/>
          <Typography
            variant="secondary14"
            color="text.primary"
            sx={{ ml: formattedWithdrawReserve.iconSymbol.split('_').length > 1 ? 3 : 1, mb: 0.5 }}
          >
            &minus;
          </Typography>
          <DarkTooltip
            wrap
            title={
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{ display: 'flex' }}>
                  <FormattedNumber
                    value={formattedWithdrawAmount}
                    variant="secondary14"
                    color="common.white"
                    sx={{ mr: 1 }}
                  />
                  <Typography variant="secondary14" color="common.white">
                    {formattedWithdrawReserve.symbol}
                  </Typography>
                </Box>
              </Box>
            }
            arrow
            placement="top"
          >
            <Box>
              <FormattedNumber
                value={formattedWithdrawAmount}
                variant="secondary14"
                color="text.primary"
                sx={{ mr: 1 }}
                compact
                compactThreshold={100000}
              />
            </Box>
          </DarkTooltip>
          <DarkTooltip
            title={
              <Typography variant="secondary14" color="common.white">
                {formattedWithdrawReserve.name} ({formattedWithdrawReserve.symbol})
              </Typography>
            }
            arrow
            placement="top"
          >
            <Typography variant="secondary14" color="text.primary">
              {formattedWithdrawReserve.symbol}
            </Typography>
          </DarkTooltip>
        </Box>
      );
    case EOperationType.Repay:
      const formattedRepayReserve = fetchIconSymbolAndNameHistorical(transaction.reserve!);
      const formattedRepayAmount = formatUnits(transaction.amount, transaction.reserve!.decimals);
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <TokenIcon symbol={formattedRepayReserve.iconSymbol} sx={{ fontSIze: iconSize }}/>
          <Typography
            variant="secondary14"
            color="text.primary"
            sx={{ ml: formattedRepayReserve.iconSymbol.split('_').length > 1 ? 3 : 1, mb: 0.5 }}
          >
            +
          </Typography>
          <DarkTooltip
            wrap
            title={
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{ display: 'flex' }}>
                  <FormattedNumber
                    value={formattedRepayAmount}
                    variant="secondary14"
                    color="common.white"
                    sx={{ mr: 1 }}
                  />
                  <Typography variant="secondary14" color="common.white">
                    {formattedRepayReserve.symbol}
                  </Typography>
                </Box>
              </Box>
            }
            arrow
            placement="top"
          >
            <Box>
              <FormattedNumber
                value={formattedRepayAmount}
                variant="secondary14"
                color="text.primary"
                sx={{ mr: 1 }}
                compact
                compactThreshold={100000}
              />
            </Box>
          </DarkTooltip>
          <DarkTooltip
            title={
              <Typography variant="secondary14" color="common.white">
                {formattedRepayReserve.name} ({formattedRepayReserve.symbol})
              </Typography>
            }
            arrow
            placement="top"
          >
            <Typography variant="secondary14" color="sucess.main">
              {formattedRepayReserve.symbol}
            </Typography>
          </DarkTooltip>
        </Box>
      );

    case EOperationType.SupplyErc1155:
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <Typography variant="secondary14" color="text.primary">
            Supply NFT with ID {transaction.erc1155TokenId}
          </Typography>
        </Box>
      );
    case EOperationType.WithdrawErc1155:
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <Typography variant="secondary14" color="text.primary">
            Withdraw NFT with ID {transaction.erc1155TokenId}
          </Typography>
        </Box>
      );
    case EOperationType.Leverage:
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <Typography variant="secondary14" color="text.primary">
            Leverage LP Position
          </Typography>
        </Box>
      );
    case EOperationType.CreateAndLeverage:
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <Typography variant="secondary14" color="text.primary">
            Create and Leverage LP Position
          </Typography>
        </Box>
      );
    case EOperationType.Deleverage:
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <Typography variant="secondary14" color="text.primary">
            Deleverage LP Position
          </Typography>
        </Box>
      );
    case EOperationType.ClaimFees:
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <Typography variant="secondary14" color="text.primary">
            Claim Fees
          </Typography>
        </Box>
      );
    case EOperationType.Compound:
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <Typography variant="secondary14" color="text.primary">
            Compound Fees
          </Typography>
        </Box>
      );
    case EOperationType.Rebalance:
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <Typography variant="secondary14" color="text.primary">
            Rebalance LP position
          </Typography>
        </Box>
      );
    case EOperationType.ClosePosition:
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <Typography variant="secondary14" color="text.primary">
            Close LP position
          </Typography>
        </Box>
      );
    case EOperationType.LeverageLiquidate:
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <Typography variant="secondary14" color="text.primary">
            Liquidation LP position
          </Typography>
        </Box>
      );
    default:
      return <></>;




    // case 'create_and_leverage':
    //   return (
    //     <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
    //       <Typography variant="secondary14" color="text.primary">
    //         Create and Leverage LP Position
    //       </Typography>
    //     </Box>
    //   );
    // case 'supply':
    //   const supplyTx = transaction as ITransactionHistoryItem;
    //   const formattedSupplyReserve = fetchIconSymbolAndNameHistorical(supplyTx.reserve!);
    //   const formattedSupplyAmount = formatUnits(supplyTx.amount, supplyTx.reserve!.decimals);
    //   return (
    //     <Box sx={{ display: 'flex', alignItems: 'center' }}>
    //       <TokenIcon symbol={formattedSupplyReserve.iconSymbol} sx={{ fontSize: iconSize }}/>
    //       <Typography
    //         variant="secondary14"
    //         color="text.primary"
    //         sx={{ ml: formattedSupplyReserve.iconSymbol.split('_').length > 1 ? 3 : 1, mb: 0.5 }}
    //       >
    //         +
    //       </Typography>
    //       <DarkTooltip
    //         wrap
    //         title={
    //           <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    //             <Box sx={{ display: 'flex' }}>
    //               <FormattedNumber
    //                 value={formattedSupplyAmount}
    //                 variant="secondary14"
    //                 color="common.white"
    //                 sx={{ mr: 1 }}
    //               />
    //               <Typography variant="secondary14" color="common.white">
    //                 {formattedSupplyReserve.symbol}
    //               </Typography>
    //             </Box>
    //           </Box>
    //         }
    //         arrow
    //         placement="top"
    //       >
    //         <Box>
    //           <FormattedNumber
    //             value={formattedSupplyAmount}
    //             variant="secondary14"
    //             color="text.primary"
    //             compact
    //             compactThreshold={100000}
    //             sx={{ mr: 1 }}
    //           />
    //         </Box>
    //       </DarkTooltip>
    //       <DarkTooltip
    //         title={
    //           <Typography variant="secondary14" color="common.white">
    //             {formattedSupplyReserve.name} ({formattedSupplyReserve.symbol})
    //           </Typography>
    //         }
    //         arrow
    //         placement="top"
    //       >
    //         <Typography variant="secondary14" color="text.primary">
    //           {formattedSupplyReserve.symbol}
    //         </Typography>
    //       </DarkTooltip>
    //     </Box>
    //   );
    // case 'borrow':
    //   const borrowTx = transaction as ITransactionHistoryItem<ActionFields['Borrow']>;
    //   const formattedBorrowReserve = fetchIconSymbolAndNameHistorical(borrowTx.reserve!);
    //   const formattedBorrowAmount = formatUnits(borrowTx.amount, borrowTx.reserve!.decimals);
    //   return (
    //     <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
    //       <TokenIcon symbol={formattedBorrowReserve.iconSymbol} sx={{ fontSIze: iconSize }}/>
    //       <Typography
    //         variant="secondary14"
    //         color="text.primary"
    //         sx={{ ml: formattedBorrowReserve.iconSymbol.split('_').length > 1 ? 3 : 1, mb: 0.5 }}
    //       >
    //         &minus;
    //       </Typography>
    //       <DarkTooltip
    //         wrap
    //         title={
    //           <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    //             <Box sx={{ display: 'flex' }}>
    //               <FormattedNumber
    //                 value={formattedBorrowAmount}
    //                 variant="secondary14"
    //                 color="common.white"
    //                 sx={{ mr: 1 }}
    //               />
    //               <Typography variant="secondary14" color="common.white">
    //                 {formattedBorrowReserve.symbol}
    //               </Typography>
    //             </Box>
    //           </Box>
    //         }
    //         arrow
    //         placement="top"
    //       >
    //         <Box>
    //           <FormattedNumber
    //             value={formattedBorrowAmount}
    //             variant="secondary14"
    //             color="text.primary"
    //             sx={{ mr: 1 }}
    //             compact
    //             compactThreshold={100000}
    //           />
    //         </Box>
    //       </DarkTooltip>
    //       <DarkTooltip
    //         title={
    //           <Typography variant="secondary14" color="common.white">
    //             {formattedBorrowReserve.name} ({formattedBorrowReserve.symbol})
    //           </Typography>
    //         }
    //         arrow
    //         placement="top"
    //       >
    //         <Typography variant="secondary14" color="text.primary">
    //           {formattedBorrowReserve.symbol}
    //         </Typography>
    //       </DarkTooltip>
    //     </Box>
    //   );
    // case 'withdraw':
    //   const withdrawTx = transaction as ITransactionHistoryItem<ActionFields['Withdraw']>;
    //   const formattedWithdrawReserve = fetchIconSymbolAndNameHistorical(withdrawTx.reserve!);
    //   const formattedWithdrawAmount = formatUnits(withdrawTx.amount, withdrawTx.reserve!.decimals);
    //   return (
    //     <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
    //       <TokenIcon symbol={formattedWithdrawReserve.iconSymbol} sx={{ fontSIze: iconSize }}/>
    //       <Typography
    //         variant="secondary14"
    //         color="text.primary"
    //         sx={{ ml: formattedWithdrawReserve.iconSymbol.split('_').length > 1 ? 3 : 1, mb: 0.5 }}
    //       >
    //         &minus;
    //       </Typography>
    //       <DarkTooltip
    //         wrap
    //         title={
    //           <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    //             <Box sx={{ display: 'flex' }}>
    //               <FormattedNumber
    //                 value={formattedWithdrawAmount}
    //                 variant="secondary14"
    //                 color="common.white"
    //                 sx={{ mr: 1 }}
    //               />
    //               <Typography variant="secondary14" color="common.white">
    //                 {formattedWithdrawReserve.symbol}
    //               </Typography>
    //             </Box>
    //           </Box>
    //         }
    //         arrow
    //         placement="top"
    //       >
    //         <Box>
    //           <FormattedNumber
    //             value={formattedWithdrawAmount}
    //             variant="secondary14"
    //             color="text.primary"
    //             sx={{ mr: 1 }}
    //             compact
    //             compactThreshold={100000}
    //           />
    //         </Box>
    //       </DarkTooltip>
    //       <DarkTooltip
    //         title={
    //           <Typography variant="secondary14" color="common.white">
    //             {formattedWithdrawReserve.name} ({formattedWithdrawReserve.symbol})
    //           </Typography>
    //         }
    //         arrow
    //         placement="top"
    //       >
    //         <Typography variant="secondary14" color="text.primary">
    //           {formattedWithdrawReserve.symbol}
    //         </Typography>
    //       </DarkTooltip>
    //     </Box>
    //   );
    // case 'repay':
    //   const repayTx = transaction as ITransactionHistoryItem<ActionFields['Repay']>;
    //   const formattedRepayReserve = fetchIconSymbolAndNameHistorical(repayTx.reserve!);
    //   const formattedRepayAmount = formatUnits(repayTx.amount, repayTx.reserve!.decimals);
    //   return (
    //     <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
    //       <TokenIcon symbol={formattedRepayReserve.iconSymbol} sx={{ fontSIze: iconSize }}/>
    //       <Typography
    //         variant="secondary14"
    //         color="text.primary"
    //         sx={{ ml: formattedRepayReserve.iconSymbol.split('_').length > 1 ? 3 : 1, mb: 0.5 }}
    //       >
    //         +
    //       </Typography>
    //       <DarkTooltip
    //         wrap
    //         title={
    //           <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    //             <Box sx={{ display: 'flex' }}>
    //               <FormattedNumber
    //                 value={formattedRepayAmount}
    //                 variant="secondary14"
    //                 color="common.white"
    //                 sx={{ mr: 1 }}
    //               />
    //               <Typography variant="secondary14" color="common.white">
    //                 {formattedRepayReserve.symbol}
    //               </Typography>
    //             </Box>
    //           </Box>
    //         }
    //         arrow
    //         placement="top"
    //       >
    //         <Box>
    //           <FormattedNumber
    //             value={formattedRepayAmount}
    //             variant="secondary14"
    //             color="text.primary"
    //             sx={{ mr: 1 }}
    //             compact
    //             compactThreshold={100000}
    //           />
    //         </Box>
    //       </DarkTooltip>
    //       <DarkTooltip
    //         title={
    //           <Typography variant="secondary14" color="common.white">
    //             {formattedRepayReserve.name} ({formattedRepayReserve.symbol})
    //           </Typography>
    //         }
    //         arrow
    //         placement="top"
    //       >
    //         <Typography variant="secondary14" color="sucess.main">
    //           {formattedRepayReserve.symbol}
    //         </Typography>
    //       </DarkTooltip>
    //     </Box>
    //   );
    // case 'supply_erc1155':
    //   return (
    //     <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
    //       <Typography variant="secondary14" color="text.primary">
    //         Supply NFT with ID {transaction.erc1155_token_id}
    //       </Typography>
    //     </Box>
    //   );
    // case 'withdraw_erc1155':
    //   return (
    //     <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
    //       <Typography variant="secondary14" color="text.primary">
    //         Withdraw NFT with ID {transaction.erc1155_token_id}
    //       </Typography>
    //     </Box>
    //   );
    // case 'leverage':
    //   return (
    //     <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
    //       <Typography variant="secondary14" color="text.primary">
    //         Leverage LP Position
    //       </Typography>
    //     </Box>
    //   );
    // case 'deleverage':
    //   return (
    //     <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
    //       <Typography variant="secondary14" color="text.primary">
    //         Deleverage LP Position
    //       </Typography>
    //     </Box>
    //   );
    // case 'UsageAsCollateral':
    //   const collateralUsageTx = transaction as ITransactionHistoryItem<
    //     ActionFields['UsageAsCollateral']
    //   >;
    //   const formattedCollateralReserve = fetchIconSymbolAndNameHistorical(
    //     collateralUsageTx.reserve!
    //   );
    //   return (
    //     <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
    //       <Typography variant="description" color="text.primary">
    //         <Trans>Collateralization</Trans>
    //       </Typography>
    //       {collateralUsageTx.toState === true ? (
    //         <Typography variant="subheader1" color="success.main" sx={{ px: 0.75 }}>
    //           <Trans>enabled</Trans>
    //         </Typography>
    //       ) : (
    //         <Typography variant="subheader1" color="error.main" sx={{ px: 0.75 }}>
    //           <Trans>disabled</Trans>
    //         </Typography>
    //       )}
    //       <Typography variant="description" color="text.primary" sx={{ mr: 0.5 }}>
    //         <Trans>for</Trans>
    //       </Typography>
    //       <TokenIcon
    //         symbol={formattedCollateralReserve.iconSymbol}
    //         sx={{
    //           fontSIze: iconSize,
    //         }}
    //       />
    //       <DarkTooltip
    //         title={
    //           <Typography variant="secondary14" color="common.white">
    //             {formattedCollateralReserve.name} ({formattedCollateralReserve.symbol})
    //           </Typography>
    //         }
    //         arrow
    //         placement="top"
    //       >
    //         <Typography
    //           variant="secondary14"
    //           color="text.primary"
    //           sx={{ ml: formattedCollateralReserve.iconSymbol.split('_').length > 1 ? 3 : 1 }}
    //         >
    //           {formattedCollateralReserve.symbol}
    //         </Typography>
    //       </DarkTooltip>
    //     </Box>
    //   );
    // case 'LiquidationCall':
    //   const liquidationTx = transaction as ITransactionHistoryItem<ActionFields['LiquidationCall']>;
    //   const formattedLiquidationColatReserve = fetchIconSymbolAndNameHistorical(
    //     liquidationTx.collateralReserve
    //   );
    //   const formattedLiquidationBorrowReserve = fetchIconSymbolAndNameHistorical(
    //     liquidationTx.principalReserve
    //   );
    //   const formattedCollateralAmount = formatUnits(
    //     liquidationTx.collateralAmount,
    //     liquidationTx.collateralReserve.decimals
    //   );
    //   const formattedLiquidationBorrowAmount = formatUnits(
    //     liquidationTx.principalAmount,
    //     liquidationTx.principalReserve.decimals
    //   );
    //   return (
    //     <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
    //       <Box sx={{ display: 'flex', flexDirection: 'column' }} pr={4.5}>
    //         <Typography>
    //           <Trans>Liquidated collateral</Trans>
    //         </Typography>
    //         <Box sx={{ display: 'inline-flex' }}>
    //           <TokenIcon
    //             symbol={formattedLiquidationColatReserve.iconSymbol}
    //             sx={{ fontSIze: iconSize, pr: 0.5 }}
    //           />
    //           <Box
    //             sx={{
    //               ml: formattedLiquidationColatReserve.iconSymbol.split('_').length > 1 ? 3 : 1,
    //               display: 'inline-flex',
    //             }}
    //           >
    //             <Typography
    //               variant="secondary14"
    //               color="text.primary"
    //               sx={{ display: 'inline-flex', mb: 0.5 }}
    //             >
    //               &minus;
    //             </Typography>
    //             <DarkTooltip
    //               wrap
    //               title={
    //                 <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    //                   <Box sx={{ display: 'flex' }}>
    //                     <FormattedNumber
    //                       value={formattedCollateralAmount}
    //                       variant="secondary14"
    //                       color="common.white"
    //                       sx={{ mr: 1 }}
    //                     />
    //                     <Typography variant="secondary14" color="common.white">
    //                       {formattedLiquidationColatReserve.symbol}
    //                     </Typography>
    //                   </Box>
    //                 </Box>
    //               }
    //               arrow
    //               placement="top"
    //             >
    //               <Box>
    //                 <FormattedNumber
    //                   value={formattedCollateralAmount}
    //                   variant="secondary14"
    //                   color="text.primary"
    //                   sx={{ mr: 1 }}
    //                   compact
    //                   compactThreshold={100000}
    //                 />
    //               </Box>
    //             </DarkTooltip>
    //             <DarkTooltip
    //               title={
    //                 <Typography variant="secondary14" color="common.white">
    //                   {formattedLiquidationColatReserve.name} (
    //                   {formattedLiquidationColatReserve.symbol})
    //                 </Typography>
    //               }
    //               arrow
    //               placement="top"
    //             >
    //               <Typography
    //                 variant="secondary14"
    //                 color="text.primary"
    //                 sx={{ display: 'inline-flex' }}
    //               >
    //                 {formattedLiquidationColatReserve.symbol}
    //               </Typography>
    //             </DarkTooltip>
    //           </Box>
    //         </Box>
    //       </Box>
    //       <SvgIcon sx={{ fontSize: '14px' }}>
    //         <ArrowNarrowRightIcon/>
    //       </SvgIcon>
    //       <Box sx={{ display: 'flex', flexDirection: 'column' }} pl={4.5}>
    //         <Typography>
    //           <Trans>Covered debt</Trans>
    //         </Typography>
    //         <Box sx={{ display: 'inline-flex' }}>
    //           <TokenIcon
    //             symbol={formattedLiquidationBorrowReserve.iconSymbol}
    //             sx={{ fontSIze: iconSize, pr: 0.5 }}
    //           />
    //           <Box
    //             sx={{
    //               ml: formattedLiquidationBorrowReserve.iconSymbol.split('_').length > 1 ? 3 : 1,
    //               display: 'inline-flex',
    //             }}
    //           >
    //             <Typography
    //               variant="secondary14"
    //               color="text.primary"
    //               sx={{ display: 'inline-flex', mb: 0.5 }}
    //             >
    //               +
    //             </Typography>
    //             <DarkTooltip
    //               wrap
    //               title={
    //                 <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    //                   <Box sx={{ display: 'flex' }}>
    //                     <FormattedNumber
    //                       value={formattedLiquidationBorrowAmount}
    //                       variant="secondary14"
    //                       color="common.white"
    //                       sx={{ mr: 1 }}
    //                     />
    //                     <Typography variant="secondary14" color="common.white">
    //                       {formattedLiquidationBorrowReserve.symbol}
    //                     </Typography>
    //                   </Box>
    //                 </Box>
    //               }
    //               arrow
    //               placement="top"
    //             >
    //               <Box>
    //                 <FormattedNumber
    //                   value={formattedLiquidationBorrowAmount}
    //                   variant="secondary14"
    //                   color="text.primary"
    //                   sx={{ mr: 1 }}
    //                   compact
    //                   compactThreshold={100000}
    //                 />
    //               </Box>
    //             </DarkTooltip>
    //             <DarkTooltip
    //               title={
    //                 <Typography variant="secondary14" color="common.white">
    //                   {formattedLiquidationBorrowReserve.name} (
    //                   {formattedLiquidationBorrowReserve.symbol})
    //                 </Typography>
    //               }
    //               arrow
    //               placement="top"
    //             >
    //               <Typography
    //                 variant="secondary14"
    //                 color="text.primary"
    //                 sx={{ display: 'inline-flex' }}
    //               >
    //                 {formattedLiquidationBorrowReserve.symbol}
    //               </Typography>
    //             </DarkTooltip>
    //           </Box>
    //         </Box>
    //       </Box>
    //     </Box>
    //   );
    // case 'leverage_liquidate':
    //   return (
    //     <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
    //       <Typography variant="secondary14" color="text.primary">
    //         Liquidation LP position
    //       </Typography>
    //     </Box>
    //   );
    // default:
    //   return <></>;
  }
};
